'use client';

import React, { useState, useRef, useEffect } from 'react';

interface Message {
  id: number;
  from: 'bot' | 'user';
  text: string;
}

const FAQ_SUGGESTIONS = [
  'What services do you offer?',
  'How do I get a quote?',
  'Which countries do you operate in?',
  'How do I access the MY WTO Portal?',
  'How long does shipping take?',
];

const FAQ_ANSWERS: Record<string, string> = {
  'what services do you offer':
    'WTO offers Sea Freight (LCL & FCL), Air Freight, Inland Transport, Port Operations, Customs Representation & Clearance, Cargo Insurance, and Bank Guarantee services.',
  'how do i get a quote': 'You can request a free transport quote at our Transport Quote page (/transport-quote). Our team responds the same day, Monday–Friday.',
  'which countries do you operate in': 'WTO has offices in 16 countries: Albania, Bosnia & Herzegovina, Bulgaria, China, Croatia, Germany, Greece, Kosovo, North Macedonia, Montenegro, Romania, Serbia, Slovenia, Taiwan, USA, and Vietnam.',
  'how do i access the my wto portal': 'You can request portal access via the "Request Portal Access" button on the homepage, or visit /portal-registration for more details.',
  'how long does shipping take': 'Sea freight typically takes 10–35 days depending on the route. Air freight is usually 2–7 days.',
};

function getBotReply(input: string): string {
  const lower = input.toLowerCase().trim();

  for (const key of Object.keys(FAQ_ANSWERS)) {
    if (lower.includes(key) || key.includes(lower)) {
      return FAQ_ANSWERS[key];
    }
  }

  if (lower.includes('sea') || lower.includes('lcl') || lower.includes('fcl') || lower.includes('container')) {
    return 'WTO provides both LCL (Less than Container Load) and FCL (Full Container Load) sea freight services. Transit times are typically 10–35 days. Visit /lcl-transport or /container-transport for more info.';
  }
  if (lower.includes('air') || lower.includes('airfreight')) {
    return 'WTO offers airfreight transport with typical transit times of 2–7 days. Visit /airfreight-transport for more details.';
  }
  if (lower.includes('inland') || lower.includes('road')) {
    return 'WTO provides inland road transport across the Balkans and Europe. Visit /inland-transport for more information.';
  }
  if (lower.includes('customs') || lower.includes('clearance')) {
    return 'WTO offers full customs representation and clearance services. Visit /customs-representation for details.';
  }
  if (lower.includes('insurance') || lower.includes('cargo insurance')) {
    return 'WTO provides cargo insurance to protect your shipments. Visit /cargo-insurance for more information.';
  }
  if (lower.includes('port') || lower.includes('port operations')) {
    return 'WTO handles port operations as part of our comprehensive logistics services. Visit /port-operations for details.';
  }
  if (lower.includes('bank guarantee')) {
    return 'WTO offers bank guarantee services. Visit /bank-guarantee for more information.';
  }
  if (lower.includes('contact') || lower.includes('phone') || lower.includes('email')) {
    return 'You can reach the WTO team via our Contacts page at /contacts. We\'re happy to help!';
  }
  if (lower.includes('portal') || lower.includes('mywto') || lower.includes('my wto')) {
    return 'The MY WTO Portal lets you track shipments and manage documents. Request access via the homepage or visit /portal-registration.';
  }
  if (lower.includes('quote') || lower.includes('price') || lower.includes('cost') || lower.includes('rate')) {
    return 'We offer free transport quotes! Visit /transport-quote and our team will respond the same day (Monday–Friday).';
  }
  if (lower.includes('hello') || lower.includes('hi') || lower.includes('hey')) {
    return 'Hello! How can I help you today? Feel free to ask about our services, routes, transit times, or anything else WTO-related.';
  }
  if (lower.includes('thank')) {
    return "You're welcome! Is there anything else I can help you with?";
  }

  return "I'm not sure about that. For detailed assistance, please visit our Contacts page (/contacts) or request a free quote at /transport-quote. Our team is happy to help!";
}

const WELCOME: Message = {
  id: 0,
  from: 'bot',
  text: "Hi! I'm the WTO virtual assistant. Ask me anything about our services, routes, offices, or logistics — or pick a question below.",
};

const ChatbotWidget: React.FC = () => {
  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([WELCOME]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setMounted(true);
    const styleId = 'chatbot-bounce-style';
    if (!document.getElementById(styleId)) {
      const styleEl = document.createElement('style');
      styleEl.id = styleId;
      styleEl.textContent = `
        @keyframes chatbotBounce {
          0%, 60%, 100% { transform: translateY(0); }
          30% { transform: translateY(-4px); }
        }
      `;
      document.head.appendChild(styleEl);
    }
    return () => {
      const el = document.getElementById(styleId);
      if (el) el.remove();
    };
  }, []);

  useEffect(() => {
    if (open) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      inputRef.current?.focus();
    }
  }, [open, messages]);

  const sendUserMessage = (text: string) => {
    if (!text.trim() || isTyping) return;

    const userMsg: Message = { id: Date.now(), from: 'user', text };
    setMessages((prev) => [...prev, userMsg]);
    setInputValue('');
    setIsTyping(true);

    setTimeout(() => {
      const reply = getBotReply(text);
      setMessages((prev) => [...prev, { id: Date.now() + 1, from: 'bot', text: reply }]);
      setIsTyping(false);
    }, 600);
  };

  const handleSend = () => sendUserMessage(inputValue);
  const handleFAQ = (question: string) => sendUserMessage(question);
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleSend();
  };

  const showFAQs = messages.length <= 1;

  if (!mounted) return null;

  return (
    <>
      {/* Chat window */}
      {open && (
        <div
          style={{
            position: 'fixed',
            bottom: '88px',
            right: '16px',
            zIndex: 40,
            width: 'min(340px, calc(100vw - 32px))',
            maxHeight: '520px',
            background: '#0A1628',
            border: '1px solid rgba(75,163,211,0.25)',
            boxShadow: '0 20px 60px rgba(0,0,0,0.6), 0 0 0 1px rgba(75,163,211,0.1)',
            borderRadius: '16px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 16px',
              background: 'rgba(75,163,211,0.12)',
              borderBottom: '1px solid rgba(75,163,211,0.15)',
              flexShrink: 0,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #4BA3D3 0%, #2E8B57 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
              </div>
              <div>
                <p style={{ color: 'white', fontSize: '14px', fontWeight: 600, lineHeight: 1.2, margin: 0 }}>WTO Assistant</p>
                <p style={{ color: '#3DAA6A', fontSize: '12px', margin: 0 }}>● Online</p>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.4)', padding: '4px' }}
              aria-label="Close chat"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Messages */}
          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '16px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              minHeight: 0,
            }}
          >
            {messages.map((msg) => (
              <div
                key={msg.id}
                style={{ display: 'flex', justifyContent: msg.from === 'user' ? 'flex-end' : 'flex-start' }}
              >
                <div
                  style={
                    msg.from === 'user'
                      ? {
                          maxWidth: '80%',
                          padding: '10px 14px',
                          borderRadius: '16px',
                          borderBottomRightRadius: '4px',
                          fontSize: '14px',
                          lineHeight: 1.5,
                          background: '#4BA3D3',
                          color: '#050D1A',
                        }
                      : {
                          maxWidth: '80%',
                          padding: '10px 14px',
                          borderRadius: '16px',
                          borderBottomLeftRadius: '4px',
                          fontSize: '14px',
                          lineHeight: 1.5,
                          background: 'rgba(255,255,255,0.07)',
                          color: 'rgba(255,255,255,0.85)',
                          border: '1px solid rgba(255,255,255,0.08)',
                          whiteSpace: 'pre-wrap',
                        }
                  }
                >
                  {msg.text}
                </div>
              </div>
            ))}

            {/* Typing indicator */}
            {isTyping && (
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <div
                  style={{
                    padding: '12px 16px',
                    borderRadius: '16px',
                    borderBottomLeftRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    background: 'rgba(255,255,255,0.07)',
                    border: '1px solid rgba(255,255,255,0.08)',
                  }}
                >
                  {[0, 1, 2].map((i) => (
                    <span
                      key={i}
                      style={{
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        background: 'rgba(255,255,255,0.4)',
                        display: 'inline-block',
                        animation: `chatbotBounce 1.2s ease-in-out ${i * 0.2}s infinite`,
                      }}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* FAQ quick-picks */}
            {showFAQs && !isTyping && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '4px' }}>
                {FAQ_SUGGESTIONS.map((question) => (
                  <button
                    key={question}
                    onClick={() => handleFAQ(question)}
                    style={{
                      textAlign: 'left',
                      fontSize: '12px',
                      padding: '8px 12px',
                      borderRadius: '12px',
                      background: 'rgba(75,163,211,0.1)',
                      border: '1px solid rgba(75,163,211,0.2)',
                      color: 'rgba(255,255,255,0.7)',
                      cursor: 'pointer',
                      transition: 'opacity 0.15s',
                    }}
                  >
                    {question}
                  </button>
                ))}
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px',
              borderTop: '1px solid rgba(255,255,255,0.07)',
              flexShrink: 0,
            }}
          >
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask a question…"
              disabled={isTyping}
              style={{
                flex: 1,
                background: 'transparent',
                border: 'none',
                outline: 'none',
                fontSize: '14px',
                color: 'white',
                opacity: isTyping ? 0.5 : 1,
              }}
            />
            <button
              onClick={handleSend}
              disabled={!inputValue.trim() || isTyping}
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                background: '#4BA3D3',
                border: 'none',
                cursor: inputValue.trim() && !isTyping ? 'pointer' : 'default',
                opacity: inputValue.trim() && !isTyping ? 1 : 0.3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                transition: 'opacity 0.15s',
              }}
              aria-label="Send message"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#050D1A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 2 11 13M22 2 15 22l-4-9-9-4 20-7z" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Floating toggle button */}
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          position: 'fixed',
          bottom: '16px',
          right: '16px',
          zIndex: 40,
          width: '56px',
          height: '56px',
          borderRadius: '50%',
          background: open ? 'rgba(75,163,211,0.9)' : 'linear-gradient(135deg, #4BA3D3 0%, #2E6DA3 100%)',
          boxShadow: '0 8px 30px rgba(75,163,211,0.45)',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'transform 0.2s, background 0.2s',
        }}
        aria-label={open ? 'Close chat' : 'Open chat'}
      >
        {open ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        ) : (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        )}
      </button>
    </>
  );
};

export default ChatbotWidget;
